import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider, Paper } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { fetchProgram, deleteProgramGoal } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function ProgramGoalDetailPage() {
  const { businessDomainId, businessUnitId, programId, goalId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/programs/${programId}`;

  const [goal, setGoal] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const d = await fetchProgram(programId);
      const found = (d.goals || [])[Number(goalId)];
      setGoal(found || null);
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, goalId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteProgramGoal(programId, goal);
      notify.success(t("message.delete.success"));
      navigate(`${basePath}/goals`);
    } catch (err) { notify.apiError(err); }
  };

  if (!goal) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("program.goal.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`${basePath}/goals/${goalId}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />}
            onClick={() => setDeleteOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("program.goal.indicator")} value={goal.indicator?.indicatorName} />
            {goal.indicator && (
              <Paper variant="outlined" sx={{ mt: 1, p: 2, bgcolor: "grey.50" }}>
                {[
                  { label: t("appKpi.unit"),              value: goal.indicator.unit },
                  { label: t("appKpi.measuringMethod"),    value: goal.indicator.measuringMethod },
                  { label: t("appKpi.evaluationStandard"), value: goal.indicator.evaluationStandard },
                  { label: t("appKpi.description"),        value: goal.indicator.description },
                ].filter((row) => row.value).map((row) => (
                  <Box key={row.label} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>{row.label}:</Typography>
                    <Typography variant="body2">{row.value}</Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </Grid>
          <Grid size={12}>
            <DetailField
              label={t("program.goal.plannedValue")}
              value={goal.plannedValue != null && goal.plannedValue !== ""
                ? `${goal.plannedValue}${goal.indicator?.unit ? `（${goal.indicator.unit}）` : ""}`
                : ""}
            />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("program.goal.plannedDate")} value={formatDate(goal.plannedDate)} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("program.goal.deadline")} value={formatDate(goal.deadline)} />
          </Grid>
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button variant="outlined" startIcon={<EmojiEventsIcon />}
          onClick={() => navigate(`${basePath}/goals/${goalId}/achievements`)}>
          {t("program.achievement.list.title")}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`${basePath}/goals`)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog
        open={deleteOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Box>
  );
}
